import {
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';

import * as Sentry from '@sentry/react';
import { toast } from 'sonner';

import AnalyticsService from '../services/analytics/analytics.service';
import { GetDashboardDTO } from '../services/analytics/dto/analytics.dto';
import { Dashboard } from '../types/dashboards';

export async function dashboardLoader(): Promise<GetDashboardDTO> {
  let dashboardData = await AnalyticsService.getDashboards();
  return dashboardData;
}

interface DashboardContextT {
  allDashboards: Dashboard[];
  activeDashboard: Nullable<Dashboard>;
  setActiveDashboard: Dispatch<SetStateAction<Nullable<Dashboard>>>;
  storeLocallyActiveDashboard: (dashboard: Dashboard) => void;
  loading: boolean;
}

const DashboardContext = createContext<DashboardContextT>({
  allDashboards: [],
  activeDashboard: {} as Dashboard,
  setActiveDashboard: () => {},
  storeLocallyActiveDashboard: () => {},
  loading: false
});

export const DashboardProvider: FC<{ children: ReactElement }> = ({
  children
}) => {
  const [dashboardList, setDashboardList] = useState<Dashboard[]>([]);

  const [loading, setLoading] = useState(true);

  const dashboardFromStorage = localStorage.getItem('activeDashboard');

  const dashboardParsedFromStorage = dashboardFromStorage
    ? JSON.parse(dashboardFromStorage)
    : null;
  const [activeDashboard, setActiveDashboard] = useState<Nullable<Dashboard>>(
    dashboardParsedFromStorage
  );

  const storeLocallyActiveDashboard = (dashboard: Dashboard) => {
    localStorage.setItem('activeDashboard', JSON.stringify(dashboard));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardsData = await AnalyticsService.getDashboards();
        const dashboardList = dashboardsData.dashboards;

        setDashboardList(dashboardList);
        if (activeDashboard) {
          setLoading(false);
          return;
        }

        if (dashboardList.length > 0) {
          setActiveDashboard(dashboardList[0]);
          storeLocallyActiveDashboard(dashboardList[0]);
        }
        setLoading(false);
      } catch (error) {
        toast.error('Error fetching dashboard list');

        // STMN-3697 / P5 -- this block used to read:
        //   throw new Error('Error while fetching the dashboard list')
        // and it is the reason CVE-2026-72898 went unnoticed for six weeks. Three separate
        // faults, all fixed here:
        //
        // 1. The cause was DISCARDED. Sentry received 323 events across 67 users, first
        //    seen 2025-07-31, none of which said why. The incident was eventually found by
        //    hand, by chasing this very message back to its origin. `cause` is followed by
        //    Sentry's linked-errors handling, so the HTTP status now travels with it.
        // 2. It THREW from an async function called by useEffect, with nothing awaiting it.
        //    That is an unhandled rejection, not a report: no error boundary sees it, and
        //    React cannot render anything useful from it. Reporting explicitly is both
        //    louder and better placed.
        // 3. It never released `loading`, so a failed fetch left the UI on its spinner for
        //    ever -- the failure looked like slowness, which is the least actionable
        //    appearance a broken dependency can take.
        setLoading(false);
        Sentry.captureException(
          new Error('Error while fetching the dashboard list', { cause: error })
        );
      }
    };

    fetchData();
  }, []);
  return (
    <DashboardContext.Provider
      value={{
        allDashboards: dashboardList,
        loading,
        activeDashboard,
        setActiveDashboard,
        storeLocallyActiveDashboard
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  return useContext(DashboardContext);
};
